import "flag-icons/css/flag-icons.min.css";

import { t } from "@lingui/macro";
import { ArrowRight, CaretDown, Check, CircleNotch, Copy, MagnifyingGlass, Translate, X } from "@phosphor-icons/react";
import { Dialog, DialogContent } from "@stylingcv/ui";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { cn } from "@stylingcv/utils";
import { useMemo, useState } from "react";

import { RESUMES_KEY } from "@/client/constants/query-keys";
import { toast } from "@/client/hooks/use-toast";
import { queryClient } from "@/client/libs/query-client";
import { translateResume } from "@/client/services/resume/resume";
import { useResumeStore } from "@/client/stores/resume";

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const TRANSLATE_LANGUAGES = [
  { locale: "en-US", name: "English",    native: "English",     flag: "gb" },
  { locale: "ar-SA", name: "Arabic",     native: "العربية",      flag: "sa" },
  { locale: "fr-FR", name: "French",     native: "Français",    flag: "fr" },
  { locale: "de-DE", name: "German",     native: "Deutsch",     flag: "de" },
  { locale: "es-ES", name: "Spanish",    native: "Español",     flag: "es" },
  { locale: "it-IT", name: "Italian",    native: "Italiano",    flag: "it" },
  { locale: "pt-BR", name: "Portuguese", native: "Português",   flag: "br" },
  { locale: "nl-NL", name: "Dutch",      native: "Nederlands",  flag: "nl" },
  { locale: "ru-RU", name: "Russian",    native: "Русский",     flag: "ru" },
  { locale: "zh-CN", name: "Chinese",    native: "中文",         flag: "cn" },
  { locale: "ja-JP", name: "Japanese",   native: "日本語",       flag: "jp" },
  { locale: "ko-KR", name: "Korean",     native: "한국어",       flag: "kr" },
  { locale: "hi-IN", name: "Hindi",      native: "हिन्दी",      flag: "in" },
  { locale: "tr-TR", name: "Turkish",    native: "Türkçe",      flag: "tr" },
  { locale: "pl-PL", name: "Polish",     native: "Polski",      flag: "pl" },
  { locale: "sv-SE", name: "Swedish",    native: "Svenska",     flag: "se" },
  { locale: "da-DK", name: "Danish",     native: "Dansk",       flag: "dk" },
  { locale: "fi-FI", name: "Finnish",    native: "Suomi",       flag: "fi" },
  { locale: "nb-NO", name: "Norwegian",  native: "Norsk",       flag: "no" },
];

const LOCALE_TO_NAME: Record<string, string> = Object.fromEntries(
  TRANSLATE_LANGUAGES.map((l) => [l.locale, l.name]),
);

export const TranslateResumeModal = ({ isOpen, onClose }: Props) => {
  const [selectedLocale, setSelectedLocale] = useState<string>("");
  const [search, setSearch] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);

  const resumeId = useResumeStore((state) => state.resume.id);
  const resumeTitle = useResumeStore((state) => state.resume.title);
  const currentLocale = useResumeStore((state) => state.resume.data.metadata?.locale || "en-US");

  const sourceLanguage = LOCALE_TO_NAME[currentLocale] || "English";
  const sourceLang = TRANSLATE_LANGUAGES.find((l) => l.locale === currentLocale);
  const selectedLang = TRANSLATE_LANGUAGES.find((l) => l.locale === selectedLocale);

  const filteredLangs = useMemo(() => {
    const q = search.trim().toLowerCase();
    const available = TRANSLATE_LANGUAGES.filter((l) => l.locale !== currentLocale);
    if (!q) return available;
    return available.filter(
      (l) =>
        l.name.toLowerCase().includes(q) ||
        l.native.toLowerCase().includes(q),
    );
  }, [search, currentLocale]);

  const handleClose = () => {
    if (isTranslating) return;
    setSelectedLocale("");
    setSearch("");
    setPickerOpen(false);
    onClose();
  };

  const handleSelect = (locale: string) => {
    setSelectedLocale(locale);
    setPickerOpen(false);
    setSearch("");
  };

  const handleTranslate = async () => {
    if (!selectedLocale) {
      toast({
        title: t`Select Language`,
        description: t`Please select a target language for translation.`,
        variant: "error",
      });
      return;
    }

    const targetLanguage = LOCALE_TO_NAME[selectedLocale];
    setIsTranslating(true);

    try {
      const result = await translateResume({
        sourceResumeId: resumeId,
        targetLanguage,
        sourceLanguage,
      });

      const store = useResumeStore.getState();
      useResumeStore.setState({
        resume: { ...store.resume, data: result.data, updatedAt: result.updatedAt },
      });

      queryClient.invalidateQueries({ queryKey: RESUMES_KEY });

      toast({
        title: t`Translation Complete`,
        description: t`Your resume has been translated to ${targetLanguage}.`,
      });

      handleClose();
    } catch (error: any) {
      toast({
        title: t`Translation Failed`,
        description: error?.response?.data?.message || t`Failed to translate resume. Please try again.`,
        variant: "error",
      });
    } finally {
      setIsTranslating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="flex w-[calc(100vw-2rem)] max-w-[420px] flex-col gap-0 overflow-hidden p-0 [&>button:last-child]:hidden">
        <VisuallyHidden>
          <span>{t`Translate Resume`}</span>
        </VisuallyHidden>

        {/* Header */}
        <div className="flex shrink-0 items-center gap-3.5 border-b border-gray-100 bg-gradient-to-b from-white to-[#FAFAFA] px-5 pb-3.5 pt-4">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#155469] to-[#268599]">
            <Translate size={16} weight="bold" className="text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-[20px] font-bold leading-tight tracking-[-0.01em] text-[#155469] [font-family:var(--font-serif,Georgia,serif)]">
              {t`Translate Resume`}
            </h2>
            <p className="mt-0.5 text-[13px] text-gray-500">
              {t`Copy your resume to another language`}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={isTranslating}
            className="flex size-8 shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
          >
            <X size={16} />
            <span className="sr-only">{t`Close`}</span>
          </button>
        </div>

        {/* Body */}
        <div className="px-5 pt-5 pb-4">

          {/* Two-box language selector row */}
          <div className="flex items-center gap-3">

            {/* Source box */}
            <div className="flex flex-1 flex-col items-center gap-1.5 rounded-xl border border-gray-100 bg-[#FAFAFA] px-3 py-3">
              <img
                src={`https://flagcdn.com/w40/${sourceLang?.flag || "gb"}.png`}
                alt={sourceLanguage}
                className="h-5 w-7 rounded object-cover shadow-sm"
              />
              <span className="text-[12px] font-semibold text-gray-700">{sourceLanguage}</span>
              {sourceLang && (
                <span className="text-[11px] text-gray-400">{sourceLang.native}</span>
              )}
            </div>

            {/* Arrow + copy label */}
            <div className="flex flex-col items-center gap-1 shrink-0">
              <div className="flex size-8 items-center justify-center rounded-full border border-dashed border-[#268599]/40 bg-[rgba(38,133,153,0.06)]">
                <Copy size={13} className="text-[#268599]" />
              </div>
              <ArrowRight size={14} className="text-[#268599]/60" />
            </div>

            {/* Target box — clickable */}
            <button
              type="button"
              onClick={() => setPickerOpen((v) => !v)}
              className={cn(
                "flex flex-1 flex-col items-center gap-1.5 rounded-xl border px-3 py-3 transition-all",
                selectedLang
                  ? "border-[#268599]/30 bg-[rgba(38,133,153,0.04)]"
                  : pickerOpen
                    ? "border-[#268599] bg-[rgba(38,133,153,0.04)]"
                    : "border-dashed border-gray-300 bg-white hover:border-[#268599]/50",
              )}
            >
              {selectedLang ? (
                <>
                  <img
                    src={`https://flagcdn.com/w40/${selectedLang.flag}.png`}
                    alt={selectedLang.name}
                    className="h-5 w-7 rounded object-cover shadow-sm"
                  />
                  <span className="text-[12px] font-semibold text-[#268599]">{selectedLang.name}</span>
                  <span className="text-[11px] text-gray-400">{selectedLang.native}</span>
                </>
              ) : (
                <>
                  <div className="flex size-7 items-center justify-center rounded-full bg-gray-100">
                    <CaretDown size={12} className={cn("text-gray-400 transition-transform", pickerOpen && "rotate-180")} />
                  </div>
                  <span className="text-[12px] font-medium text-gray-400">{t`Select language`}</span>
                  <span className="text-[11px] text-gray-300">{t`Target`}</span>
                </>
              )}
              {selectedLang && (
                <CaretDown size={11} className={cn("text-[#268599]/50 transition-transform", pickerOpen && "rotate-180")} />
              )}
            </button>
          </div>

          {/* Language picker — slides open */}
          {pickerOpen && (
            <div className="mt-3 rounded-xl border border-gray-100 overflow-hidden">
              {/* Search */}
              <div className="relative border-b border-gray-100 bg-white">
                <MagnifyingGlass
                  size={13}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
                <input
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t`Search languages…`}
                  className="h-9 w-full bg-transparent pl-8 pr-3 text-[13px] text-gray-800 placeholder:text-gray-400 outline-none"
                />
              </div>
              {/* List */}
              <div className="max-h-[180px] overflow-y-auto">
                {filteredLangs.length === 0 ? (
                  <p className="px-4 py-4 text-center text-[13px] text-gray-400">{t`No languages match`}</p>
                ) : (
                  filteredLangs.map((lang) => {
                    const isSelected = lang.locale === selectedLocale;
                    return (
                      <button
                        key={lang.locale}
                        type="button"
                        onClick={() => handleSelect(lang.locale)}
                        className={cn(
                          "flex w-full items-center gap-3 border-b border-gray-50 px-3 py-2 text-left last:border-0 transition-colors",
                          isSelected ? "bg-[rgba(38,133,153,0.06)]" : "bg-white hover:bg-gray-50",
                        )}
                      >
                        <img
                          src={`https://flagcdn.com/w40/${lang.flag}.png`}
                          alt={lang.name}
                          className="h-3.5 w-5 shrink-0 rounded-sm object-cover"
                        />
                        <span className={cn("flex-1 text-[13px] font-medium", isSelected ? "text-[#268599]" : "text-gray-800")}>
                          {lang.name}
                        </span>
                        <span className="text-[12px] text-gray-400">{lang.native}</span>
                        {isSelected && <Check size={12} weight="bold" className="shrink-0 text-[#268599]" />}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-gray-100 bg-[#FAFAFA] px-5 py-[14px]">
          <span className="text-[12px] font-semibold text-gray-400">{t`Costs 150 AI tokens`}</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={isTranslating}
              className="cursor-pointer rounded-[9px] border border-gray-300 bg-white px-[18px] py-[9px] text-[13px] font-semibold text-gray-900 transition-colors hover:border-gray-400 disabled:opacity-40"
            >
              {t`Cancel`}
            </button>
            <button
              type="button"
              disabled={!selectedLocale || isTranslating}
              onClick={handleTranslate}
              className="flex cursor-pointer items-center gap-1.5 rounded-[9px] bg-gradient-to-br from-[#155469] via-[#268599] to-[#E8885E] px-[18px] py-[9px] text-[13px] font-bold text-white shadow-[0_4px_14px_rgba(38,133,153,0.30)] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {isTranslating ? (
                <>
                  <CircleNotch size={14} className="animate-spin" />
                  {t`Translating...`}
                </>
              ) : (
                <>
                  <Translate size={14} weight="bold" />
                  {t`Translate`}
                </>
              )}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
